<script setup lang="ts">
import { reactive, onMounted, ref } from 'vue';
import { ExternalLink, ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Project {
  url: string;
  title: string;
  appStore?: string;
  imgSrc?: string;
  images?: string[];
  summaryKey: string;
  descriptionKeys: string[];
  tags: string[];
  links?: { label: string; href: string }[];
  type: 'professional' | 'personal';
}

type IframeState = 'loading' | 'loaded' | 'failed';
const iframeStatus = reactive<Record<string, IframeState>>({});
const carouselIndexes = reactive<Record<string, number>>({});
const activeTab = ref<'professional' | 'personal'>('professional');

const projects: Project[] = [
  {
    url: 'https://pubmates.co.uk',
    title: 'pubmates',
    appStore: 'https://apps.apple.com/gb/app/pubmates/id6765459261',
    imgSrc: 'https://og-image.vercel.app/pubmates.co.uk.png',
    summaryKey: 'projects.pubmates-summary',
    descriptionKeys: [
      'projects.pubmates-desc',
      'projects.pubmates-desc-2',
      'projects.pubmates-desc-3',
      'projects.pubmates-desc-4',
    ],
    tags: ['React Native', 'Expo SDK 54', 'Supabase', 'Next.js', 'Stripe', 'TypeScript'],
    type: 'professional',
  },
  {
    url: 'https://www.goose-fulham.com/',
    title: 'The Goose Fulham',
    imgSrc: 'https://og-image.vercel.app/goose-fulham.com.png',
    summaryKey: 'projects.goose-fulham-summary',
    descriptionKeys: ['projects.goose-fulham-summary'],
    tags: ['Next.js', 'Tailwind CSS', 'DesignMyNight'],
    type: 'professional',
  },
  {
    url: 'https://heikocbd.com/',
    title: 'Heiko CBD',
    imgSrc: 'https://og-image.vercel.app/heikocbd.com.png',
    summaryKey: 'projects.heiko-cbd-summary',
    descriptionKeys: [
      'projects.heiko-cbd-desc',
      'projects.heiko-cbd-desc-2',
      'projects.heiko-cbd-desc-3',
    ],
    tags: ['Vue.js', 'Tailwind CSS', 'E-commerce'],
    type: 'professional',
  },
  {
    url: 'https://heikoeducation.com/journals',
    title: 'Heiko Education',
    imgSrc: 'https://og-image.vercel.app/heikoeducation.com.png',
    summaryKey: 'projects.heiko-education-summary',
    descriptionKeys: [
      'projects.heiko-education-desc',
      'projects.heiko-education-desc-2',
      'projects.heiko-education-desc-3',
    ],
    tags: ['Vue.js', 'Tailwind CSS', 'Content Platform'],
    type: 'professional',
  },
  {
    url: 'https://github.com/Brunosil97/rain-or-shine-frontend',
    title: 'Rain Or Shine',
    imgSrc: 'https://github.com/Brunosil97/rain-or-shine-frontend/blob/main/src/assets/dashboard.png?raw=true',
    summaryKey: 'projects.rain-or-shine-summary',
    descriptionKeys: [
      'projects.rain-or-shine-desc',
      'projects.rain-or-shine-desc-2',
      'projects.rain-or-shine-desc-3',
      'projects.rain-or-shine-desc-4',
    ],
    tags: ['Vue 3', 'Vite', 'Deno 2', 'Vuetify', 'OpenWeatherMap'],
    links: [
      { label: 'Frontend', href: 'https://github.com/Brunosil97/rain-or-shine-frontend' },
      { label: 'Backend', href: 'https://github.com/Brunosil97/rain-or-shine-backend' },
    ],
    type: 'personal',
  },
  {
    url: 'https://github.com/Brunosil97/bookhub-frontend',
    title: 'BookHub',
    images: [
      'https://github.com/Brunosil97/bookhub-frontend/blob/main/public/landing_page.png?raw=true',
      'https://github.com/Brunosil97/bookhub-frontend/blob/main/public/grid_view.png?raw=true',
      'https://github.com/Brunosil97/bookhub-frontend/blob/main/public/my_books.png?raw=true',
      'https://github.com/Brunosil97/bookhub-frontend/blob/main/public/analytics.png?raw=true',
      'https://github.com/Brunosil97/bookhub-frontend/blob/main/public/settings.png?raw=true',
    ],
    summaryKey: 'projects.bookhub-summary',
    descriptionKeys: [
      'projects.bookhub-desc',
      'projects.bookhub-desc-2',
      'projects.bookhub-desc-3',
      'projects.bookhub-desc-4',
    ],
    tags: ['Vue 3', 'TypeScript', 'Vuetify', '.NET 8', 'Pinia', 'Vitest'],
    links: [
      { label: 'Frontend', href: 'https://github.com/Brunosil97/bookhub-frontend' },
      { label: 'Backend', href: 'https://github.com/Brunosil97/bookhub-backend' },
    ],
    type: 'personal',
  },
  {
    url: 'https://brunosil97.github.io/whos-better-at-rivals/',
    title: "Who's Better?",
    images: [
      'https://github.com/Brunosil97/whos-better-at-rivals/blob/main/public/landing_page.png?raw=true',
      'https://github.com/Brunosil97/whos-better-at-rivals/blob/main/public/rankings.png?raw=true',
      'https://github.com/Brunosil97/whos-better-at-rivals/blob/main/public/table.png?raw=true',
      'https://github.com/Brunosil97/whos-better-at-rivals/blob/main/public/recent_matches.png?raw=true',
    ],
    summaryKey: 'projects.whos-better-summary',
    descriptionKeys: [
      'projects.whos-better-desc',
      'projects.whos-better-desc-2',
      'projects.whos-better-desc-3',
    ],
    tags: ['Vue 3', 'TypeScript', 'Vuetify 3', 'Pinia', 'IndexedDB', 'Marvel Rivals API'],
    links: [
      { label: 'Live', href: 'https://brunosil97.github.io/whos-better-at-rivals/' },
      { label: 'GitHub', href: 'https://github.com/Brunosil97/whos-better-at-rivals' },
    ],
    type: 'personal',
  },
];

onMounted(() => {
  projects.forEach((p) => {
    iframeStatus[p.title] = 'loading';
    if (p.images?.length) carouselIndexes[p.title] = 0;
  });
});

function nextSlide(title: string, total: number) {
  carouselIndexes[title] = (carouselIndexes[title] + 1) % total;
}

function prevSlide(title: string, total: number) {
  carouselIndexes[title] = carouselIndexes[title] === 0 ? total - 1 : carouselIndexes[title] - 1;
}

const proProjects = projects.filter((p) => p.type === 'professional');
const perProjects = projects.filter((p) => p.type === 'personal');
</script>

<template>
  <div class="container mx-auto p-4 space-y-8">
    <h1 class="text-4xl font-bold">{{ t("domain.projects") }}</h1>

    <!-- Tabs -->
    <div class="join">
      <button
        class="btn btn-soft btn-primary btn-sm sm:btn-md"
        :class="{ 'btn-active': activeTab === 'professional' }"
        @click="activeTab = 'professional'"
      >
        {{ t("common.professional") }}
      </button>
      <button
        class="btn btn-soft btn-primary btn-sm sm:btn-md"
        :class="{ 'btn-active': activeTab === 'personal' }"
        @click="activeTab = 'personal'"
      >
        {{ t("common.personal") }}
      </button>
    </div>

    <!-- Project list -->
    <div class="space-y-8">
      <template v-for="project in activeTab === 'professional' ? proProjects : perProjects" :key="project.title">
        <div class="card bg-base-100 border border-base-300 shadow-sm">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-0">

            <!-- Left: mockup with iframe or image carousel -->
            <div class="mockup-browser bg-base-200 lg:col-span-2 rounded-b-none lg:rounded-r-none lg:rounded-bl-2xl">
              <div class="mockup-browser-toolbar">
                <div class="input text-xs truncate">{{ project.url }}</div>
              </div>

              <!-- iframe (professional projects with live URL) -->
              <div v-if="project.type === 'professional'" class="relative h-[480px]">
                <div
                  v-if="iframeStatus[project.title] === 'loading'"
                  class="absolute inset-0 flex justify-center items-center"
                >
                  <span class="loading loading-spinner text-primary" />
                </div>
                <iframe
                  v-if="iframeStatus[project.title] !== 'failed'"
                  v-show="iframeStatus[project.title] === 'loaded'"
                  :src="project.url"
                  class="w-full h-full"
                  frameborder="0"
                  @load="iframeStatus[project.title] = 'loaded'"
                  @error="iframeStatus[project.title] = 'failed'"
                />
                <img
                  v-if="iframeStatus[project.title] === 'failed'"
                  :src="project.imgSrc"
                  :alt="project.title"
                  class="w-full h-full object-cover object-top"
                />
              </div>

              <!-- Image carousel (personal projects) -->
              <div v-else class="relative h-[480px] overflow-hidden">
                <img
                  :src="(project.images ?? [project.imgSrc!])[carouselIndexes[project.title] ?? 0]"
                  :alt="project.title"
                  class="w-full h-full object-cover object-top transition-opacity duration-300"
                />
                <template v-if="(project.images?.length ?? 0) > 1">
                  <button
                    class="absolute left-2 top-1/2 -translate-y-1/2 btn btn-circle btn-sm btn-ghost bg-base-100/70 hover:bg-base-100"
                    @click="prevSlide(project.title, project.images!.length)"
                  >
                    <ChevronLeft :size="16" />
                  </button>
                  <button
                    class="absolute right-2 top-1/2 -translate-y-1/2 btn btn-circle btn-sm btn-ghost bg-base-100/70 hover:bg-base-100"
                    @click="nextSlide(project.title, project.images!.length)"
                  >
                    <ChevronRight :size="16" />
                  </button>
                  <div class="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    <div
                      v-for="(_, i) in project.images"
                      :key="i"
                      class="w-2 h-2 rounded-full transition-colors cursor-pointer"
                      :class="i === (carouselIndexes[project.title] ?? 0) ? 'bg-primary' : 'bg-base-content/30'"
                      @click="carouselIndexes[project.title] = i"
                    />
                  </div>
                </template>
              </div>
            </div>

            <!-- Right: description panel -->
            <div class="flex flex-col gap-4 p-6 h-[528px]">
              <div class="shrink-0">
                <h3 class="text-2xl font-bold mb-3">{{ project.title }}</h3>
                <div class="flex flex-wrap gap-2">
                  <!-- Professional: live + app store -->
                  <template v-if="project.type === 'professional'">
                    <a :href="project.url" target="_blank" class="btn btn-primary btn-sm">
                      {{ t("common.live") }}
                      <ExternalLink :size="14" />
                    </a>
                    <a
                      v-if="project.appStore"
                      :href="project.appStore"
                      target="_blank"
                      class="btn btn-primary btn-sm"
                    >
                      {{ t("common.app-store") }}
                      <ExternalLink :size="14" />
                    </a>
                  </template>
                  <!-- Personal: custom links -->
                  <template v-else>
                    <a
                      v-for="link in project.links"
                      :key="link.label"
                      :href="link.href"
                      target="_blank"
                      class="btn btn-primary btn-sm"
                    >
                      {{ link.label }}
                      <ExternalLink :size="14" />
                    </a>
                  </template>
                </div>
              </div>

              <div class="overflow-y-auto flex-1 space-y-3 pr-1">
                <p
                  v-for="key in project.descriptionKeys"
                  :key="key"
                  class="text-base-content/70 text-sm leading-relaxed"
                >
                  {{ t(key) }}
                </p>
              </div>

              <div class="shrink-0 flex flex-wrap gap-2 pt-2 border-t border-base-300">
                <span v-for="tag in project.tags" :key="tag" class="badge badge-outline badge-sm">
                  {{ tag }}
                </span>
              </div>
            </div>

          </div>
        </div>
      </template>
    </div>
  </div>
</template>
